import "bootstrap/dist/css/bootstrap.min.css";
import "@/styles/globals.css";
import "@/styles/swiper.css";
import { Toaster } from "react-hot-toast";
import type { AppProps } from "next/app";
import Script from "next/script";
import { initMixpanel, trackPageView } from "@/services/mixpanel";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import {
  usePageExitTracking,
  useScrollTracking,
  useSessionEndTracking,
  useSessionStartTracking,
  useTimeSpentTracking,
} from "@/services/mixpanelFunctionality";
import { Cormorant_Garamond } from "next/font/google";
import FloatingActionButton from "@/components/FloatingActionButton/FloatingActionButton";

const cormorantGaramond = Cormorant_Garamond({
  subsets: ["latin"],
  weight: ["300", "400", "500", "600", "700"],
  style: ["normal", "italic"],
  display: "optional",
});

export default function App({ Component, pageProps }: AppProps) {
  const router = useRouter();
  const {
    utm_source,
    utm_medium,
    utm_campaign,
    utm_content,
    utm_term,
    utm_id,
  } = router.query;

  const salesForceUrl = process.env.VIN_CRM_API_BASE_URL;
  const leadsGenerateUrl = process.env.LEADS_API_BASE_URL;

  // State and refs for session tracking
  const [sessionStartTime, setSessionStartTime] = useState<string>("");
  const totalPagesViewed = useRef<number>(0);
  const [sessionEngagementScore, setSessionEngagementScore] =
    useState<number>(0);

  useEffect(() => {
    const startTime = new Date().toISOString();
    setSessionStartTime(startTime);
  }, []);

  useEffect(() => {
    totalPagesViewed.current += 1;
    setSessionEngagementScore((prev) => prev + 10);
  }, [router.asPath]);

  useEffect(() => {
    initMixpanel();
    trackPageView();

    const handleRouteChange = () => trackPageView();
    router.events.on("routeChangeComplete", handleRouteChange);

    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, []);

  // Track user behavior
  useSessionStartTracking();
  useScrollTracking();
  usePageExitTracking();
  useTimeSpentTracking();
  useSessionEndTracking(
    sessionStartTime,
    totalPagesViewed,
    sessionEngagementScore
  );

  return (
    <>
      <style jsx global>{`
        :root {
          --font-cormo: ${cormorantGaramond.style.fontFamily};
        }
      `}</style>

      <Component {...pageProps} />

      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            borderRadius: "12px",
            padding: "12px 20px",
            fontWeight: 400,
            fontSize: "14px",
            width: "100%",
            maxWidth: "402px",
          },
          duration: 3000,

          success: {
            style: {
              border: "1px solid #008744",
              background: "#E5F5E8",
              color: "#2B3340",
            },
          },
          error: {
            style: {
              border: "1px solid #D92D20",
              background: "#FEE2E2",
              color: "#7F1D1D",
            },
          },
        }}
      />

      <div id="tawk_5825dfc218d9f16af02abeea"></div>
      {!router.pathname.includes("/lp") && (
        <>
          <Script
            id="Tawk_API"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `
      var Tawk_API = Tawk_API || {};
      var Tawk_LoadStart = new Date();
      (function(){
        var s1 = document.createElement('script'), s0 = document.getElementsByTagName('script')[0];
        s1.async = true;
        s1.src = 'https://embed.tawk.to/5825dfc218d9f16af02abeea/1cpccedaj';
        s1.charset = 'UTF-8';
        s1.setAttribute('crossorigin', '*');
        s0.parentNode.insertBefore(s1, s0);
      })();
 
      window.Tawk_API.onPrechatSubmit = function(data){
        const salesForceUrl = '${salesForceUrl}';
        const leadForceUrl = '${leadsGenerateUrl}';
        const utmSource = '${utm_source ? utm_source : "SA Website Chat"}';
        const utmCompaign = '${utm_campaign ? utm_campaign : "SA_Website"}';
        const utmContent = '${utm_content ? utm_content : "SA Website Chat"}';
        const utmMedium = '${utm_medium ? utm_medium : "SA Website"}';
        const utmTerm = '${utm_term ? utm_term : "SA Website"}';
        const utmId = '${utm_id ? utm_id : "SA Website"}';
 
        const salesForceData = {
          domain: "crm",
          type: "add_lead_to_crm",
          name: "",
          email: "",
          country: "",
          mobile: "",
          interest: "",
          source: utmSource,
          campaign: utmCompaign,
          utm_source: utmSource,
          utm_medium: utmMedium,
          utm_campaign: utmCompaign,
          utm_content: utmContent,
          utm_term: utmTerm,
          utm_id: utmId,
          utm_url: window?.location?.href,
          Source_Campaign: 'SA Website',
          Lead_Source: "SA Website Chat"
        };

        const leadsData = {
          Email: "",
          Name: "",
          Phone: "",
          Programme_Of_Interest: "",
          Interested_Topic: "",
          date: new Date().toLocaleDateString(),
          page_url: window?.location?.href,
          utm_parameters: window?.location?.href,
          source: utmSource,
          campaign: utmCompaign,
          utm_source: utmSource,
          utm_medium: utmMedium,
          utm_campaign: utmCompaign,
          utm_content: utmContent,
          utm_term: utmTerm,
          utm_id: utmId,
          utm_url: window?.location?.href,
          Source_Campaign: 'SA Website',
          Lead_Source: "SA Website Chat"
        };

        const getCountryCode = (countryName) => {
          const codes = {
            "Nigeria": "+234",
            "Kenya": "+254",
            "Tanzania": "+255",
            "Uganda": "+256",
            "Others": "+27"
          };
          return codes[countryName] || "+27";
        };
 
        data.forEach(item => {
          switch (item.label) {
            case "Name":
              salesForceData.name = item.answer;
              leadsData.Name = item.answer;
              break;
            case "Email":
              salesForceData.email = item.answer;
              leadsData.Email = item.answer;
              break;
            case "Country":
              salesForceData.country = item.answer === "Others" ? "South Africa" : item.answer;
              break;
            case "Mobile Number":
              const country = salesForceData.country;
              const mobile = item.answer.replace(/\s+/g, '');
              const countryCode = getCountryCode(country);
              const formattedMobile = mobile.startsWith("+") ? mobile : countryCode + mobile;

              if (formattedMobile.length < 9 || formattedMobile.length > 16) {
                console.error("Invalid phone number length. It should be between 9 and 16 digits.");
                return; // Skip lead submission if invalid
              }

              salesForceData.mobile = formattedMobile;
              leadsData.Phone = formattedMobile;
              break;
            case "Programme of Interest":
              salesForceData.interest = item.answer;
              leadsData.Interested_Topic = item.answer;
              leadsData.Programme_Of_Interest = item.answer;
              break;
          }
        });

        if (salesForceData.mobile) { 
          fetch(salesForceUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(salesForceData)
          })
          .then(response => response.json())
          .then(() => generateNewLead())
          .catch(console.error);

          const generateNewLead = () => {
            fetch(leadForceUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(leadsData)
            })
            .then(response => response.json())
            .then(console.log)
            .catch(console.error);
          };
        }
      };
    `,
            }}
          />
          <FloatingActionButton />
        </>
      )}
    </>
  );
}
